import { RGBAColor, StepOption } from "../types/wizard";

/**
 * Converts an sRGB value to a linear RGB value.
 */
function sRgbToLinear(value: number): number {
  return value / 255 <= 0.04045
    ? value / 255 / 12.92
    : Math.pow((value / 255 + 0.055) / 1.055, 2.4);
}

/**
 * Converts RGB to XYZ color space.
 */
function rgbToXyz(r: number, g: number, b: number): { x: number; y: number; z: number } {
  r = sRgbToLinear(r);
  g = sRgbToLinear(g);
  b = sRgbToLinear(b);

  return {
    x: (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) * 100,
    y: (r * 0.2126729 + g * 0.7151522 + b * 0.0721750) * 100,
    z: (r * 0.0193339 + g * 0.1191920 + b * 0.9503041) * 100,
  };
}

/**
 * Converts XYZ to LAB color space.
 */
function xyzToLab(x: number, y: number, z: number): { l: number; a: number; b: number } {
  const refX = 95.047, refY = 100.0, refZ = 108.883;
  x /= refX;
  y /= refY;
  z /= refZ;

  const f = (val: number) => val > 0.008856 ? Math.cbrt(val) : (7.787 * val) + (16 / 116);

  const l = (116 * f(y)) - 16;
  const a = 500 * (f(x) - f(y));
  const b = 200 * (f(y) - f(z));

  return { l, a, b };
}

/**
 * Converts an RGB color to LAB.
 */
function rgbToLab(r: number, g: number, b: number): { l: number; a: number; b: number } {
  const xyz = rgbToXyz(r, g, b);
  return xyzToLab(xyz.x, xyz.y, xyz.z);
}

/**
 * Calculates the Euclidean distance in the LAB color space.
 */
function colorDistanceLab(color1: { l: number; a: number; b: number }, color2: { l: number; a: number; b: number }): number {
  return Math.sqrt(
    Math.pow(color1.l - color2.l, 2) +
    Math.pow(color1.a - color2.a, 2) +
    Math.pow(color1.b - color2.b, 2)
  );
}

/**
 * Converts HEX to RGB.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let sanitizedHex = hex.startsWith("#") ? hex.slice(1) : hex;
  
  if (sanitizedHex.length === 3) {
    sanitizedHex = sanitizedHex.split("").map((char) => char + char).join("");
  }

  if (sanitizedHex.length !== 6) return null;

  const bigint = parseInt(sanitizedHex, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

/**
 * Finds the closest color in a palette using LAB color distance.
 */
export function findClosestColor(targetRgba: RGBAColor, palette: StepOption[]): StepOption {
  let closestColor: StepOption | null = null;
  let smallestDistance = Infinity;

  const targetLab = rgbToLab(targetRgba.r, targetRgba.g, targetRgba.b);

  for (const entry of palette) {
    if (!entry?.htmlColor) continue;

    const paletteRgb = hexToRgb(entry.htmlColor);
    if (!paletteRgb) continue;

    const paletteLab = rgbToLab(paletteRgb.r, paletteRgb.g, paletteRgb.b);
    const distance = colorDistanceLab(targetLab, paletteLab);

    if (distance < smallestDistance) {
      smallestDistance = distance;
      closestColor = entry;
    }
  }

  if (!closestColor) {
    throw new Error("No closest color found in the palette.");
  }

  return closestColor;
}